import { adminCountsSchema, ROLE_LABELS, type AdminCapability, type AdminCounts } from "@studyward/shared";
import { cx, NavSheet } from "@studyward/ui";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  ClipboardList,
  CreditCard,
  Cpu,
  DollarSign,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Menu,
  Megaphone,
  Settings,
  ShieldCheck,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router";
import { api } from "../lib/api";
import { signOut, useAdmin } from "../lib/session";
import { INTERNAL_NOTE, Wordmark } from "./ui";

type Badge = "open_flags" | "failed_jobs" | "open_tickets";
interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  capability: AdminCapability;
  badge?: Badge;
}

/** The ten views (§10.3) plus the audit log, grouped like the mock. A role sees only what it can open. */
export const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, capability: "dashboard" },
      { to: "/users", label: "Users", icon: Users, capability: "users.view" },
    ],
  },
  {
    group: "Trust & content",
    items: [
      { to: "/moderation", label: "Moderation", icon: ShieldCheck, capability: "moderation", badge: "open_flags" },
      { to: "/jobs", label: "AI operations", icon: Cpu, capability: "jobs.view", badge: "failed_jobs" },
      { to: "/content", label: "Announcements", icon: Megaphone, capability: "content" },
    ],
  },
  {
    group: "Business",
    items: [
      { to: "/revenue", label: "Subscriptions", icon: CreditCard, capability: "revenue.view" },
      { to: "/pricing", label: "Pricing & plans", icon: DollarSign, capability: "pricing.view" },
      { to: "/programmes", label: "Programmes & exams", icon: BookOpen, capability: "programmes" },
      { to: "/support", label: "Support", icon: LifeBuoy, capability: "support", badge: "open_tickets" },
    ],
  },
  {
    group: "System",
    items: [
      { to: "/settings", label: "Settings & system", icon: Settings, capability: "settings" },
      { to: "/audit-log", label: "Audit log", icon: ClipboardList, capability: "audit_log" },
    ],
  },
];

export function Shell({ children }: { children: ReactNode }) {
  const me = useAdmin();
  const counts = useQuery({ queryKey: ["admin", "counts"], queryFn: () => api.get("/admin/counts", adminCountsSchema), refetchInterval: 60_000 });
  const groups = NAV.map((g) => ({ ...g, items: g.items.filter((i) => me.capabilities.includes(i.capability)) })).filter((g) => g.items.length > 0);
  const [menuOpen, setMenuOpen] = useState(false);
  const waiting = groups.flatMap((g) => g.items).reduce((n, i) => n + (i.badge ? (counts.data?.[i.badge] ?? 0) : 0), 0);
  const nav = <NavGroups groups={groups} counts={counts.data} onNavigate={() => setMenuOpen(false)} />;
  const account = (
    <div className="flex flex-col items-start gap-1">
      <p className="w-full truncate text-small font-semibold text-text-primary">{me.display_name || me.email || "Admin"}</p>
      <p className="w-full truncate text-caption text-text-muted">{ROLE_LABELS[me.role]}</p>
      <button type="button" onClick={() => void signOut()} className="-ml-1 mt-1 flex min-h-11 min-w-11 items-center gap-3 px-1 text-small font-medium text-text-primary hover:underline">
        <LogOut size={18} aria-hidden="true" /> Sign out
      </button>
    </div>
  );

  return (
    <div className="min-h-dvh bg-surface-background text-text-primary">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-50 focus:rounded-control focus:bg-surface-card focus:p-3 focus:shadow-raised"
      >
        Skip to content
      </a>
      <div className="mx-auto flex min-h-dvh max-w-[1560px] flex-col lg:flex-row">
        {/* Phone and tablet: frosted-glass slim bar; the grouped navigation opens in a sheet. */}
        <header className="glass-header sticky top-2 z-20 mx-3 mt-2 flex items-center gap-2 rounded-card py-2 pl-3 pr-2 shadow-card lg:hidden">
          <Link to="/" aria-label="Admin dashboard" className="flex min-h-11 min-w-0 items-center">
            <Wordmark compact />
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            className="ml-auto flex min-h-10 shrink-0 pointer-coarse:min-h-11 items-center gap-2 rounded-pill px-2.5 text-small font-semibold text-text-primary hover:bg-surface-subtle"
          >
            <Menu size={20} aria-hidden="true" />
            Menu
            {waiting > 0 && (
              <span className="rounded-pill bg-primary px-2 text-caption font-bold text-text-on-primary">
                {waiting}
                <span className="sr-only"> waiting</span>
              </span>
            )}
          </button>
        </header>
        <NavSheet open={menuOpen} onClose={() => setMenuOpen(false)} title="Admin menu" className="lg:hidden">
          {nav}
          <div className="mt-auto border-t border-border pt-4">{account}</div>
        </NavSheet>

        {/* Desktop whisper-quiet hairline sidebar: scrolls if the window is short, so every view stays reachable. */}
        <aside className="glass-sidebar hidden w-60 shrink-0 flex-col gap-5 px-4 py-6 lg:sticky lg:top-0 lg:flex lg:h-dvh lg:overflow-y-auto">
          <Link to="/" aria-label="Admin dashboard" className="inline-flex shrink-0 items-center px-2 pointer-coarse:min-h-11">
            <Wordmark compact />
          </Link>
          {nav}
          {/* One row in the rail so all eleven views and Sign out fit a 720px-tall laptop screen. */}
          <div className="mt-auto flex shrink-0 items-center gap-2 border-t border-border px-2 pt-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-small font-semibold text-text-primary">{me.display_name || me.email || "Admin"}</p>
              <p className="truncate text-caption text-text-muted">{ROLE_LABELS[me.role]}</p>
            </div>
            <button
              type="button"
              onClick={() => void signOut()}
              aria-label="Sign out"
              title="Sign out"
              className="grid size-10 shrink-0 place-items-center rounded-pill text-text-secondary hover:bg-surface-subtle hover:text-text-primary"
            >
              <LogOut size={18} aria-hidden="true" />
            </button>
          </div>
        </aside>
        <div className="flex min-w-0 flex-1 flex-col px-4 py-4 sm:px-6 sm:py-6 lg:px-9 lg:py-7">
          <main id="main" className="flex-1">
            {children}
          </main>
          <footer className="pt-10 pb-4 text-caption text-text-muted">{INTERNAL_NOTE}</footer>
        </div>
      </div>
    </div>
  );
}

function NavGroups({ groups, counts, onNavigate }: { groups: typeof NAV; counts: AdminCounts | undefined; onNavigate: () => void }) {
  return (
    <nav aria-label="Admin" className="flex shrink-0 flex-col gap-4">
      {groups.map((g) => (
        <div key={g.group} className="flex flex-col gap-0.5">
          <p className="px-3 pb-1 text-caption font-semibold uppercase tracking-wider text-text-muted">{g.group}</p>
          {g.items.map(({ to, label, icon: Icon, badge }) => {
            const n = badge ? counts?.[badge] : null;
            return (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cx(
                    "group flex min-h-11 items-center gap-3 rounded-xl px-3 text-small font-medium transition-all lg:min-h-9 pointer-coarse:min-h-11",
                    isActive
                      ? "bg-surface-card font-semibold text-text-primary shadow-card"
                      : "text-text-secondary hover:bg-surface-card/60 hover:text-text-primary",
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      size={17}
                      aria-hidden="true"
                      className={cx("shrink-0 transition-colors", isActive ? "text-primary" : "text-text-muted group-hover:text-text-primary")}
                    />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    {!!n && (
                      <span className="rounded-pill bg-primary px-2 text-caption font-bold text-text-on-primary">
                        {n}
                        <span className="sr-only"> waiting</span>
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
